import React from 'react';

import {
  Button,
  Icon,
  Separator,
  Stack,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  tv,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';

const styles = tv({
  slots: {
    container:
      'group cursor-pointer rounded-md border border-border-primary bg-bg-primary p-2 transition-colors hover:border-border-secondary hover:bg-bg-secondary',
    divider:
      'bg-border-primary transition-colors group-hover:bg-border-secondary',
  },
  variants: {
    selected: {
      true: {
        container:
          'cursor-default border-border-tertiary bg-bg-secondary hover:border-border-tertiary',
        divider: 'border-text-disabled group-hover:border-text-disabled',
      },
    },
  },
});

export type TemplateVariant = {
  variantNumber: number;
  isCurrentDefault: boolean;
  variantNotes: string | null;
  createdAt: string;
  createdBy?: string;
  isDraft?: boolean;
};

type TemplateVariantCardProps = {
  isSelected: boolean;
  onClick: () => void;
  onDuplicateClick?: () => void;
  onSetAsDefaultClick?: () => void;
  onVariantEditClick?: () => void;
  variant: TemplateVariant;
  viewOnly?: boolean;
};

export const TemplateVariantCard: React.FC<TemplateVariantCardProps> = ({
  isSelected,
  onClick,
  onDuplicateClick,
  onSetAsDefaultClick,
  onVariantEditClick,
  variant,
  viewOnly = false,
}) => {
  const { container, divider } = styles({
    selected: isSelected,
  });

  const handleDuplicateClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onDuplicateClick) {
      onDuplicateClick();
    }
  };

  const handleSetAsDefaultClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onSetAsDefaultClick) {
      onSetAsDefaultClick();
    }
  };

  const handleVariantEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onVariantEditClick) {
      onVariantEditClick();
    }
  };

  const hasActions =
    onDuplicateClick || onSetAsDefaultClick || onVariantEditClick;

  return (
    <Stack className={container()} gap={1} onClick={onClick}>
      <Stack justify="between" gap={2} direction="horizontal">
        <Stack gap={3} direction="horizontal">
          <Tag label={`# ${variant.variantNumber}`} />
          {variant.isCurrentDefault && (
            <Tag label="Default" color="lightgreen" size="s" />
          )}
          {variant.isDraft && <Tag label="Draft" color="warning" size="s" />}
        </Stack>
        {hasActions && (
          <Stack
            align="end"
            gap={1}
            inline
            fullWidth={false}
            direction="horizontal"
          >
            {!variant.isDraft && onSetAsDefaultClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    disabled={variant.isCurrentDefault || viewOnly}
                    onClick={handleSetAsDefaultClick}
                    aria-label={`Set variant ${variant.variantNumber} as default`}
                  >
                    <Icon name="Gem" size="auto" />
                  </Button>
                </TooltipTrigger>
                {!variant.isCurrentDefault && (
                  <TooltipContent sideOffset={4}>Set as default</TooltipContent>
                )}
              </Tooltip>
            )}
            {!variant.isDraft && onDuplicateClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={handleDuplicateClick}
                    disabled={viewOnly}
                    aria-label={`Duplicate variant ${variant.variantNumber}`}
                  >
                    <Icon name="CopyPlus" size="auto" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Use this to create a new variant
                </TooltipContent>
              </Tooltip>
            )}

            {variant.isDraft && onVariantEditClick && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={handleVariantEditClick}
                    disabled={viewOnly}
                    aria-label={`Edit draft variant ${variant.variantNumber}`}
                  >
                    <Icon name="Pencil" size="auto" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Edit draft variant</TooltipContent>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
      <Stack className="mx-1 mb-1">
        <Text color="secondary" weight="regular">
          {formatToLocale(variant.createdAt, { separator: '-' })}
        </Text>
        {variant.createdBy && (
          <Text color="secondary" weight="regular">
            {`By ${variant.createdBy}`}
          </Text>
        )}
      </Stack>
      {variant.variantNotes && (
        <>
          <Separator className={divider()} />
          <Text size="xs" weight="regular" className="line-clamp-2">
            {variant.variantNotes}
          </Text>
        </>
      )}
    </Stack>
  );
};
